import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, RotateCcw, Save } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type BetaSettings = {
  enabled: boolean;
  automatic_approval_limit: number;
  starting_credits: number;
  onboarding_email_subject: string;
  onboarding_email_body: string;
  onboarding_calendly_url: string;
};

type BetaStatus = {
  campaign_id: string | null;
  campaign_started_at: string | null;
  automatic_approval_limit: number;
  automatically_approved_count: number;
  remaining_automatic_approvals: number;
  pending_applications: number;
  completed_registrations: number;
};

type BetaCampaign = {
  id: string;
  started_at: string;
  ended_at: string | null;
  enabled: boolean;
  automatic_approval_limit: number;
  starting_credits: number;
};

type RegisteredBetaUser = {
  id: string;
  name: string | null;
  email: string | null;
  registered_at: string;
  approval_origin: "automatic" | "manual" | null;
  promised_starting_credits: number | null;
  beta_campaign_id: string | null;
  campaign: BetaCampaign | null;
};

const DEFAULT_SETTINGS: BetaSettings = {
  enabled: false,
  automatic_approval_limit: 10,
  starting_credits: 600,
  onboarding_email_subject: "Welcome to Applyt",
  onboarding_email_body: "Welcome to Applyt.\n\nYour beta access is ready and your account has been credited.\n\nWe would like to spend 15 minutes with you to show you how Applyt works and hear about what you want to accomplish.\n\nSchedule your onboarding conversation here:\n\n{{calendly_url}}",
  onboarding_calendly_url: "",
};

const EMPTY_STATUS: BetaStatus = {
  campaign_id: null,
  campaign_started_at: null,
  automatic_approval_limit: 0,
  automatically_approved_count: 0,
  remaining_automatic_approvals: 0,
  pending_applications: 0,
  completed_registrations: 0,
};

const registrationDateFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

const formatRegistrationDate = (registeredAt: string) => {
  const date = new Date(registeredAt);
  return Number.isNaN(date.getTime()) ? "Unknown" : registrationDateFormatter.format(date);
};

const formatApprovalOrigin = (origin: RegisteredBetaUser["approval_origin"]) => {
  if (origin === "automatic") return "Automatic";
  if (origin === "manual") return "Manual";
  return "Unknown";
};

export const BetaAccess = () => {
  const [settings, setSettings] = useState<BetaSettings>(DEFAULT_SETTINGS);
  const [status, setStatus] = useState<BetaStatus>(EMPTY_STATUS);
  const [registeredUsers, setRegisteredUsers] = useState<RegisteredBetaUser[]>([]);
  const [campaigns, setCampaigns] = useState<BetaCampaign[]>([]);
  const [campaignFilter, setCampaignFilter] = useState("all");
  const [registeredUsersLimit, setRegisteredUsersLimit] = useState(100);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [restartOpen, setRestartOpen] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke("admin-beta-access", {
      body: { action: "list", campaign: campaignFilter },
    });
    if (error || data?.error) {
      toast({ title: "Could not load Beta Access", description: error?.message || data?.error, variant: "destructive" });
    } else {
      setSettings({ ...DEFAULT_SETTINGS, ...(data?.settings ?? {}) });
      setStatus({ ...EMPTY_STATUS, ...(data?.status ?? {}) });
      setRegisteredUsers(data?.registered_users ?? []);
      setCampaigns(data?.campaigns ?? []);
      setRegisteredUsersLimit(data?.registered_users_limit ?? 100);
    }
    setLoading(false);
  }, [campaignFilter, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const update = <K extends keyof BetaSettings>(key: K, value: BetaSettings[K]) => {
    setSettings((current) => ({ ...current, [key]: value }));
  };

  const save = async () => {
    setSaving(true);
    const { data, error } = await supabase.functions.invoke("admin-beta-access", {
      body: { action: "update", settings, campaign: campaignFilter },
    });
    if (error || data?.error) {
      toast({ title: "Could not save Beta Access", description: error?.message || data?.error, variant: "destructive" });
    } else {
      setSettings({ ...DEFAULT_SETTINGS, ...(data?.settings ?? settings) });
      setStatus({ ...EMPTY_STATUS, ...(data?.status ?? status) });
      setRegisteredUsers(data?.registered_users ?? []);
      setCampaigns(data?.campaigns ?? []);
      setRegisteredUsersLimit(data?.registered_users_limit ?? 100);
      toast({ title: "Beta Access saved", description: "New applications will use the updated policy." });
    }
    setSaving(false);
  };

  const restart = async () => {
    setRestarting(true);
    const { data, error } = await supabase.functions.invoke("admin-beta-access", { body: { action: "restart" } });
    if (error || data?.error) {
      toast({ title: "Could not restart Beta Access", description: error?.message || data?.error, variant: "destructive" });
    } else {
      setRestartOpen(false);
      toast({ title: "Beta campaign restarted", description: "Automatic approvals are available again for the new campaign." });
      await load();
    }
    setRestarting(false);
  };

  if (loading) return <div className="p-8 text-center text-muted-foreground">Loading Beta Access…</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Beta Access</h2>
          <p className="text-muted-foreground">Control how new beta applications are approved and welcomed.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setRestartOpen(true)} disabled={saving || restarting} className="gap-2">
            <RotateCcw className="h-4 w-4" /> Restart campaign
          </Button>
          <Button variant="outline" onClick={() => void load()} disabled={saving || restarting} className="gap-2">
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Current campaign status</CardTitle>
          <CardDescription>
            These counters are calculated on the server from stored applications and approvals.
            {status.campaign_started_at ? ` Current campaign started ${formatRegistrationDate(status.campaign_started_at)}.` : ""}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg border p-4">
            <p className="text-sm text-muted-foreground">Automatic approvals</p>
            <p className="mt-1 text-2xl font-semibold">{status.automatically_approved_count} / {status.automatic_approval_limit}</p>
          </div>
          <div className="rounded-lg border p-4">
            <p className="text-sm text-muted-foreground">Remaining</p>
            <p className="mt-1 text-2xl font-semibold">{status.remaining_automatic_approvals}</p>
          </div>
          <div className="rounded-lg border p-4">
            <p className="text-sm text-muted-foreground">Pending applications</p>
            <p className="mt-1 text-2xl font-semibold">{status.pending_applications}</p>
          </div>
          <div className="rounded-lg border p-4">
            <p className="text-sm text-muted-foreground">Completed registrations</p>
            <p className="mt-1 text-2xl font-semibold">{status.completed_registrations}</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle>Registered beta users</CardTitle>
              <CardDescription>Showing up to {registeredUsersLimit} most recently completed registrations.</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="beta-campaign-filter" className="text-sm text-muted-foreground">Campaign</Label>
              <select
                id="beta-campaign-filter"
                value={campaignFilter}
                onChange={(event) => setCampaignFilter(event.target.value)}
                className="h-9 rounded-md border bg-background px-3 text-sm"
              >
                <option value="all">All campaigns</option>
                <option value="current">Current campaign</option>
                {campaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {formatRegistrationDate(campaign.started_at)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {registeredUsers.length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
              No beta users have completed registration yet.
            </div>
          ) : (
            <>
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full text-left text-sm">
                  <caption className="sr-only">Most recently registered beta users</caption>
                  <thead className="border-b text-muted-foreground">
                    <tr>
                      <th scope="col" className="px-3 py-2 font-medium">User</th>
                      <th scope="col" className="px-3 py-2 font-medium">Registered</th>
                      <th scope="col" className="px-3 py-2 font-medium">Source</th>
                      <th scope="col" className="px-3 py-2 font-medium">Campaign</th>
                      <th scope="col" className="px-3 py-2 text-right font-medium">Starting credits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registeredUsers.map((user) => (
                      <tr key={user.id} className="border-b last:border-0">
                        <td className="px-3 py-3">
                          <p className="font-medium text-foreground">{user.name || "Unnamed user"}</p>
                          <p className="text-muted-foreground">{user.email || "No email"}</p>
                        </td>
                        <td className="px-3 py-3 text-muted-foreground">{formatRegistrationDate(user.registered_at)}</td>
                        <td className="px-3 py-3"><Badge variant="outline">{formatApprovalOrigin(user.approval_origin)}</Badge></td>
                        <td className="px-3 py-3 text-muted-foreground">{user.campaign ? formatRegistrationDate(user.campaign.started_at) : "Unknown"}</td>
                        <td className="px-3 py-3 text-right font-medium">{user.promised_starting_credits ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="space-y-3 md:hidden">
                {registeredUsers.map((user) => (
                  <article key={user.id} className="rounded-lg border p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium text-foreground">{user.name || "Unnamed user"}</p>
                        <p className="text-sm text-muted-foreground">{user.email || "No email"}</p>
                      </div>
                      <Badge variant="outline">{formatApprovalOrigin(user.approval_origin)}</Badge>
                    </div>
                    <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <dt className="text-muted-foreground">Registered</dt>
                        <dd className="mt-1">{formatRegistrationDate(user.registered_at)}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">Starting credits</dt>
                        <dd className="mt-1 font-medium">{user.promised_starting_credits ?? 0}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">Campaign</dt>
                        <dd className="mt-1">{user.campaign ? formatRegistrationDate(user.campaign.started_at) : "Unknown"}</dd>
                      </div>
                    </dl>
                  </article>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Configuration</CardTitle>
          <CardDescription>Changes apply to future applications. Existing pending applications stay pending.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div>
              <Label htmlFor="beta-auto-approval">Automatic approval</Label>
              <p className="mt-1 text-sm text-muted-foreground">Automatically invite new applicants while capacity remains.</p>
            </div>
            <Switch id="beta-auto-approval" checked={settings.enabled} onCheckedChange={(value) => update("enabled", value)} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="beta-limit">Automatic approval limit</Label>
              <Input id="beta-limit" type="number" min="0" step="1" value={settings.automatic_approval_limit} onChange={(event) => update("automatic_approval_limit", Number(event.target.value))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="beta-credits">Starting credits</Label>
              <Input id="beta-credits" type="number" min="0" step="1" value={settings.starting_credits} onChange={(event) => update("starting_credits", Number(event.target.value))} />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="beta-subject">Onboarding email subject</Label>
            <Input id="beta-subject" value={settings.onboarding_email_subject} onChange={(event) => update("onboarding_email_subject", event.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="beta-body">Onboarding email body</Label>
            <Textarea id="beta-body" rows={9} value={settings.onboarding_email_body} onChange={(event) => update("onboarding_email_body", event.target.value)} />
            <p className="text-xs text-muted-foreground">Supported variable: <Badge variant="outline" className="font-mono">{'{{calendly_url}}'}</Badge></p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="beta-calendly">Calendly URL</Label>
            <Input id="beta-calendly" type="url" placeholder="https://calendly.com/…" value={settings.onboarding_calendly_url} onChange={(event) => update("onboarding_calendly_url", event.target.value)} />
          </div>

          <div className="flex justify-end">
            <Button onClick={() => void save()} disabled={saving} className="gap-2">
              <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={restartOpen} onOpenChange={(open) => !restarting && setRestartOpen(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restart the beta campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              This starts a new campaign and restores the automatic approval limit. Existing users, invitations, credits, and campaign history will be preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restarting}>Keep current campaign</AlertDialogCancel>
            <AlertDialogAction onClick={(event) => { event.preventDefault(); void restart(); }} disabled={restarting}>
              {restarting ? "Restarting…" : "Restart campaign"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
